import { Badge, Button, Tooltip, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SortBy } from '@hero/atoms';
import { useGetCollectionsHelper, useQueries } from '@hero/hooks';
import { OnboardingCard } from '@hero/molecules';
import { BrowseCollectionMoreInformationModal } from '@hero/organisms';
import { useGetHomeTopCategories } from '@hero/services/merchant-service/hooks';
import { useGetCategories } from '@hero/services/product-service/hooks';
import { useOnboardingStore } from '@hero/store/hooks';
import { formatCurrency } from '@hero/utils/helper';

const ProductListHeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  margin-top: 24px;
`;

const LeftContent = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RightContent = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

function ProductListHeader({
  total = 0,
  featureType,
  setIsCardWidgetOpen,
  selectAllProducts,
  showSelectAllProductsButton,
  isFetching
}) {
  const { t } = useTranslation();
  const rightContentRef = React.useRef(null);

  const { queries, addQuery, removeQueries, resetQueries } = useQueries();
  const { sort = '', minPrice = '', maxPrice = '', collectionIds = [] } = queries;
  const categoryIds =
    typeof queries.categoryIds === 'string' ? [queries.categoryIds] : queries.categoryIds;

  const { allCollections } = useGetCollectionsHelper();
  const selectedCollections = allCollections?.filter(o => collectionIds.includes(o.id));

  const { data: categoriesData } = useGetCategories(null, {
    staleTime: Infinity,
    cacheTime: Infinity
  });

  const { data: homeTopCategories } = useGetHomeTopCategories();

  const allCategories = useMemo(() => {
    const allParentCategories = categoriesData?.docs?.map(c => ({
      id: c.id,
      name: c.name
    }));
    const allSubCategories = categoriesData?.docs
      ?.map(c => c.subCategories?.map(sc => sc))
      .flat()
      .filter(Boolean);
    return [...(allParentCategories || []), ...(allSubCategories || [])];
  }, [categoriesData?.docs]);

  const selectedCategories = allCategories?.filter(c => categoryIds?.includes(c.id)) || [];
  const selectedHomeTopCategories =
    homeTopCategories?.filter(c => categoryIds?.includes(c.id)) || [];
  const set = new Set();
  const mergedCategories = [...selectedCategories, ...selectedHomeTopCategories].filter(
    c => !set.has(c.id) && set.add(c.id)
  );

  const onboarding = useOnboardingStore();
  const [isProductsOnboardingCardShown, setIsProductsOnboardingCardShown] = useState(true); // TODO: It will be true
  const [showMoreInformationModal, setShowMoreInformationModal] = useState(false);

  const infoCardContent = (
    <Button variant="link" onClick={() => setShowMoreInformationModal(true)}>
      {t('moreInformation')}
    </Button>
  );

  const hasFilter = !!(minPrice || maxPrice) || !!collectionIds?.length || !!categoryIds?.length;

  return (
    <ProductListHeaderWrapper>
      <LeftContent>
        {!isFetching && (
          <Typography variant="body2">
            <strong>{total}</strong>&nbsp;
            {t('itemsFound').toLocaleLowerCase()}
          </Typography>
        )}
        <OnboardingCard
          styles={{
            wrapper: {
              right: -26,
              top: -10
            }
          }}
          cardOffset={[20, 24]}
          position="right-start"
          isCardShown={isProductsOnboardingCardShown}
          title={t('pickYourGifts')}
          description={t('productListHeaderOnboardingCardDesc')}
          content={infoCardContent}
          setIsCardShown={setIsProductsOnboardingCardShown}
          onTourButtonClick={() => {
            setIsCardWidgetOpen(true);
          }}
          isInfoActive={onboarding?.[featureType]?.infoCardsActive}
          featureType={featureType}
        />

        <BrowseCollectionMoreInformationModal
          isModalOpen={showMoreInformationModal}
          onRequestClose={() => setShowMoreInformationModal(false)}
        />

        {(maxPrice || minPrice) && (
          <>
            <VerticalDivider $margin="0 8px" $height="24px" />
            <Badge
              variant="contained"
              color="bgWeak"
              closable
              size="sm"
              onClose={() => {
                removeQueries(['minPrice', 'maxPrice']);
              }}
              rounded
            >
              {minPrice && maxPrice && (
                <>
                  {formatCurrency(minPrice)} - {formatCurrency(maxPrice)}
                </>
              )}
              {minPrice && !maxPrice && (
                <>
                  {formatCurrency(minPrice)} and {t('above')}
                </>
              )}
              {!minPrice && maxPrice && (
                <>
                  {formatCurrency(maxPrice)} and {t('below')}
                </>
              )}
            </Badge>
          </>
        )}
        {!!selectedCollections.length && (
          <>
            <VerticalDivider $margin="0 8px" $height="24px" />
            {selectedCollections.map(collection => {
              return (
                <Badge
                  variant="contained"
                  color="bgWeak"
                  key={collection.id}
                  closable
                  size="sm"
                  onClose={() => {
                    addQuery(
                      'collectionIds',
                      selectedCollections.filter(cl => cl.id !== collection.id).map(cl => cl.id)
                    );
                  }}
                  rounded
                >
                  {collection.name}
                </Badge>
              );
            })}
          </>
        )}
        {!!mergedCategories?.length && (
          <>
            <VerticalDivider $margin="0 8px" $height="24px" />
            {mergedCategories?.map(category => {
              return (
                <Badge
                  variant="contained"
                  color="bgWeak"
                  key={category.id}
                  closable
                  size="sm"
                  onClose={() => {
                    addQuery(
                      'categoryIds',
                      categoryIds?.filter(cId => cId !== category.id)
                    );
                  }}
                  rounded
                >
                  {category?.prefix && <b>{category?.prefix}:&nbsp;</b>}
                  {category?.name}
                </Badge>
              );
            })}
          </>
        )}
        {hasFilter && (
          <>
            <Button
              variant="link"
              color="secondary"
              onClick={() => {
                resetQueries();
              }}
            >
              {t('clearAll')}
            </Button>
          </>
        )}
      </LeftContent>

      <RightContent ref={rightContentRef}>
        {showSelectAllProductsButton && (
          <Tooltip content={t('addAllBelowItems')} size="lg" tooltipConfig={{ placement: 'top' }}>
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                selectAllProducts();
              }}
            >
              {t('addAll')}
            </Button>
          </Tooltip>
        )}
        <SortBy
          value={sort}
          onChange={data => addQuery('sort', data.value)}
          rightContentRef={rightContentRef}
        />
      </RightContent>
    </ProductListHeaderWrapper>
  );
}

ProductListHeader.propTypes = {
  total: PropTypes.number,
  featureType: PropTypes.string,
  setIsCardWidgetOpen: PropTypes.func,
  selectAllProducts: PropTypes.func,
  showSelectAllProductsButton: PropTypes.bool,
  isFetching: PropTypes.bool
};

export { ProductListHeader };
